import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
    BookOpenIcon,
    ChatBubbleLeftRightIcon,
    SparklesIcon,
    XMarkIcon,
    ArrowLeftIcon,
} from "@heroicons/react/24/outline";
import useAuthorization from "@/hooks/useAuthorization";
import { isMcpConfigured } from "@/api/baseMcpApi";
import HelpArticleList from "./HelpArticleList";
import HelpArticleView from "./HelpArticleView";
import HelpContactForm from "./HelpContactForm";
import HelpAIChat from "./HelpAIChat";
import { findArticleForPath } from "./useHelpForRoute";
import type { HelpArticle } from "./types";

export type HelpDrawerMode = "general" | "page";

type Tab = "articles" | "contact" | "ai";

interface HelpDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    mode: HelpDrawerMode;
    initialTab?: Tab;
    initialTicketId?: string | null;
}

const HelpDrawer: React.FC<HelpDrawerProps> = ({
    isOpen,
    onClose,
    mode,
    initialTab,
    initialTicketId = null,
}) => {
    const { t } = useTranslation();
    const location = useLocation();
    const { hasRole } = useAuthorization();
    const isAdmin = hasRole("ROLE_ADMIN");
    const aiEnabled = isAdmin && isMcpConfigured;

    const [tab, setTab] = useState<Tab>(initialTab ?? "articles");
    const [selectedArticle, setSelectedArticle] = useState<HelpArticle | null>(null);
    const [contactTicketId, setContactTicketId] = useState<string | null>(initialTicketId);

    useEffect(() => {
        if (!isOpen) return;
        setTab(initialTab ?? "articles");
        setContactTicketId(initialTicketId);
        if (mode === "page") {
            setSelectedArticle(findArticleForPath(location.pathname, isAdmin));
        } else {
            setSelectedArticle(null);
        }
    }, [isOpen, mode, location.pathname, isAdmin, initialTab, initialTicketId]);

    if (!isOpen) return null;

    const handleBack = () => setSelectedArticle(null);

    const showBackButton = mode === "general" && tab === "articles" && selectedArticle !== null;

    return (
        <>
            <div
                className="fixed inset-0 bg-black/30 z-[9998]"
                onClick={onClose}
                aria-hidden="true"
            />
            <aside
                className="fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white shadow-2xl z-[9999] flex flex-col"
                role="dialog"
                aria-label={t("help.title")}
            >
                <header className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                        {showBackButton && (
                            <button
                                type="button"
                                onClick={handleBack}
                                className="text-gray-500 hover:text-gray-800"
                                aria-label={t("common.back")}
                            >
                                <ArrowLeftIcon className="w-5 h-5" />
                            </button>
                        )}
                        <h2 className="text-xl font-semibold">
                            {selectedArticle && tab === "articles"
                                ? t(selectedArticle.titleKey)
                                : t("help.title")}
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-800"
                        aria-label={t("common.close")}
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </header>

                <nav className="flex border-b border-gray-200">
                    <button
                        type="button"
                        onClick={() => setTab("articles")}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition ${
                            tab === "articles"
                                ? "text-blue-600 border-b-2 border-blue-600"
                                : "text-gray-500 hover:text-gray-800"
                        }`}
                    >
                        <BookOpenIcon className="w-4 h-4" />
                        {t("help.tabs.articles")}
                    </button>
                    <button
                        type="button"
                        onClick={() => setTab("contact")}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition ${
                            tab === "contact"
                                ? "text-blue-600 border-b-2 border-blue-600"
                                : "text-gray-500 hover:text-gray-800"
                        }`}
                    >
                        <ChatBubbleLeftRightIcon className="w-4 h-4" />
                        {t("help.tabs.contact")}
                    </button>
                    {aiEnabled && (
                        <button
                            type="button"
                            onClick={() => setTab("ai")}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition ${
                                tab === "ai"
                                    ? "text-blue-600 border-b-2 border-blue-600"
                                    : "text-gray-500 hover:text-gray-800"
                            }`}
                        >
                            <SparklesIcon className="w-4 h-4" />
                            {t("help.tabs.ai")}
                        </button>
                    )}
                </nav>

                <div className="flex-1 overflow-hidden flex flex-col">
                    {tab === "articles" && (
                        <>
                            {mode === "page" && selectedArticle && (
                                <div className="flex-1 overflow-y-auto">
                                    <HelpArticleView article={selectedArticle} />
                                </div>
                            )}
                            {mode === "general" && selectedArticle && (
                                <div className="flex-1 overflow-y-auto">
                                    <HelpArticleView article={selectedArticle} />
                                </div>
                            )}
                            {mode === "general" && !selectedArticle && (
                                <HelpArticleList onSelect={setSelectedArticle} />
                            )}
                        </>
                    )}
                    {tab === "contact" && (
                        <HelpContactForm
                            initialTicketId={contactTicketId}
                            onTicketSelected={setContactTicketId}
                        />
                    )}
                    {tab === "ai" && aiEnabled && <HelpAIChat visible={tab === "ai"} />}
                </div>
            </aside>
        </>
    );
};

export default HelpDrawer;
